import { API_BASE_URL } from '../config';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import sessionManager from '../utils/sessionManager';

const LoginPage = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user is already logged in
    const token = localStorage.getItem('token');
    if (token) {
      // Check if session is still valid
      sessionManager.checkSessionStatus().then(status => {
        if (status.valid) {
          navigate('/dashboard');
        } else {
          // Clear invalid session
          sessionManager.clearSession();
        }
      });
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });
      let data = {};
      try {
        data = await response.json();
      } catch (jsonErr) {
        setError('Invalid JSON response');
        return;
      }
      if (response.ok && data.token) {
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        
        // Initialize session manager with new session
        sessionManager.extendSession();
        
        navigate('/dashboard');
      } else {
        setError(data.message || `Login failed: ${response.status} ${response.statusText}`);
      }
    } catch (err) {
      setError(`Network error: ${err.message}`);
    }
  };

  return (
    <div className="login-screen">
      <style>{`
        .login-screen {
          min-height: 100vh;
          display: flex;
          flex-direction: column;
          background: var(--login-screen, #f3f6f4);
        }
        .demo-banner {
          background: #6b3a12;
          color: #fff8ee;
          padding: 0.85rem 1.5rem;
          font-size: 0.92rem;
          line-height: 1.45;
        }
        .demo-banner strong {
          display: block;
          letter-spacing: 0.04em;
          margin-bottom: 0.2rem;
        }
        .login-body {
          flex: 1;
          display: flex;
        }
        .login-ad {
          flex: 1.3;
          color: #f6f1e7;
          background:
            radial-gradient(circle at 12% 18%, rgba(212, 168, 83, 0.28), transparent 28%),
            linear-gradient(160deg, var(--login-from, #16382f) 0%, var(--login-mid, #1f4d40) 55%, var(--login-deep, #0f2923) 100%);
          padding: 4.5rem 4rem;
          display: flex;
          flex-direction: column;
          justify-content: center;
        }
        .login-kicker {
          letter-spacing: 0.16em;
          text-transform: uppercase;
          font-size: 0.78rem;
          color: var(--login-kicker, #e7c98a);
          margin: 0 0 1rem;
        }
        .login-ad h1 {
          font-size: 3rem;
          line-height: 1.1;
          margin: 0 0 0.75rem;
          font-weight: 650;
        }
        .login-product {
          margin: 0 0 1.5rem;
          font-size: 1.15rem;
          color: #d7e4dc;
        }
        .login-ad p {
          max-width: 38rem;
          font-size: 1.05rem;
          line-height: 1.6;
          margin: 0 0 1.75rem;
          color: #efe7d6;
        }
        .login-points {
          list-style: none;
          padding: 0;
          margin: 0 0 2.5rem;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 0.85rem 1.5rem;
        }
        .login-points li {
          padding-left: 1.1rem;
          position: relative;
          color: #f6f1e7;
        }
        .login-points li::before {
          content: "";
          width: 0.45rem;
          height: 0.45rem;
          border-radius: 50%;
          background: var(--login-kicker, #e7c98a);
          position: absolute;
          left: 0;
          top: 0.45rem;
        }
        .login-enquiry {
          margin-top: auto;
          padding-top: 1.5rem;
          border-top: 1px solid rgba(246, 241, 231, 0.2);
          color: #d7e4dc;
        }
        .login-enquiry a {
          color: var(--login-kicker, #e7c98a);
          font-weight: 650;
          text-decoration: none;
        }
        .login-column {
          flex: 0 0 420px;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 2rem;
        }
        .login-card {
          width: 100%;
          background: #fff;
          border: 1px solid #e2e8e4;
          border-radius: 12px;
          padding: 2rem;
          box-shadow: 0 12px 30px rgba(22, 56, 47, 0.08);
        }
        .login-card h2 {
          margin: 0 0 0.35rem;
          color: var(--theme-primary-dark, #16382f);
        }
        .login-card p {
          margin: 0 0 1.5rem;
          color: #5c6b64;
        }
        .login-card label {
          display: block;
          margin-bottom: 0.4rem;
          font-weight: 650;
          color: #24332d;
        }
        .login-card input {
          width: 100%;
          padding: 0.75rem;
          margin-bottom: 1.1rem;
          border: 1px solid #d5ded9;
          border-radius: 6px;
          font-size: 1rem;
          box-sizing: border-box;
        }
        .login-card input:focus {
          outline: 2px solid var(--theme-primary, #1f4d40);
          border-color: var(--theme-primary, #1f4d40);
        }
        .login-card button {
          width: 100%;
          padding: 0.8rem;
          background: var(--theme-primary, #1f4d40);
          color: #fff;
          border: none;
          border-radius: 6px;
          font-size: 1rem;
          font-weight: 650;
          cursor: pointer;
        }
        .login-card button:hover {
          background: var(--theme-primary-dark, #16382f);
        }
        .login-error {
          color: #8a2430;
          background: #fdecee;
          border: 1px solid #f3c2c8;
          border-radius: 6px;
          padding: 0.75rem;
          margin-bottom: 1rem;
          font-size: 0.92rem;
        }
        @media (max-width: 900px) {
          .login-body { flex-direction: column; }
          .login-ad { padding: 2.5rem 1.5rem 2rem; }
          .login-ad h1 { font-size: 2.2rem; }
          .login-points { grid-template-columns: 1fr; }
          .login-column { flex: none; width: 100%; box-sizing: border-box; }
        }
      `}</style>
      <div className="demo-banner" role="note">
        <strong>Demo site — for evaluation only</strong>
        This is a demonstration of the Clinic Management System, not a live clinic service.
        Nothing here is medical, legal, or professional advice, and it does not create a doctor–patient
        or any other legal relationship. Sample records must not be used for diagnosis, treatment,
        billing, or any real decision. Use of this demo is at your own risk.
      </div>
      <div className="login-body">
      <section className="login-ad">
        <p className="login-kicker">Kaya Revive Clinic</p>
        <h1>The clinic, on one desk</h1>
        <p className="login-product">Clinic Management System</p>
        <p>
          Reception books the visit, the doctor records treatment, therapy completes the procedure,
          and pharmacy and billing close the same patient record. Each role sees only its own work.
        </p>
        <ul className="login-points">
          <li>Patient records and visit history</li>
          <li>Appointment scheduling</li>
          <li>Doctor, therapist, and pharmacy desks</li>
          <li>Medicine stock, sales, and invoices</li>
        </ul>
        <div className="login-enquiry">
          <div>New customer queries</div>
          <a href="mailto:kayareviveclinic@gmail.com">kayareviveclinic@gmail.com</a>
        </div>
      </section>
      <aside className="login-column">
        <div className="login-card">
          <h2>Staff sign in</h2>
          <p>Use the username issued for your role.</p>
          <form onSubmit={handleSubmit}>
            <label htmlFor="username">Username</label>
            <input
              id="username"
              type="text"
              value={username}
              onChange={e => setUsername(e.target.value)}
              required
            />
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
            />
            {error && <div className="login-error">{error}</div>}
            <button type="submit">Login</button>
          </form>
        </div>
      </aside>
      </div>
    </div>
  );
};

export default LoginPage;
